import React, { ReactNode, useState } from 'react'
import { faUpload } from '@fortawesome/pro-regular-svg-icons'
import { faCloudArrowUp } from '@fortawesome/pro-solid-svg-icons'
import { Button } from '../../atoms/button'
import { Card, CardColors } from '../../atoms/card'
import { Dialog } from '../../molecules/dialog'
import { FileInput } from '../../atoms/file-input'
import { Icon } from '../../atoms/icon'
import { Label } from '../../atoms/label'
import { Text } from '../../atoms/text'
import { TypographyStyles } from '@/styles'

interface IDocumentUploadDialog {
  /** Optional content to display inside the card area of the dialog */
  children?: React.ReactNode

  /** Description text displayed below the card title to provide additional context */
  cardDescription?: React.ReactNode

  /** Title text displayed at the top of the card area */
  cardTitle?: string

  /**
   * Controls dialog behavior mode:
   * - true: Dialog manages its own state with internal trigger button
   * - false: Dialog is controlled via external button and state
   * This is useful for when there are multiple buttons to toggle a single dialog
   * like in tables or lists.
   */
  hasTrigger?: boolean

  /**
   * Controls dialog visibility when hasTrigger=false
   * Ignored when hasTrigger=true (uses internal state instead)
   */
  isOpen?: boolean

  /** Disables the file input to prevent file selection */
  isUploadDisabled?: boolean

  /** Label text for the dialog trigger button */
  label?: string

  /** Optional content to display next to the label */
  afterLabel?: React.ReactNode

  /** If true, only the upload button will be shown. */
  uploadButtonOnly?: boolean

  /** Callback fired when dialog is closed via Cancel button or dismiss
   * Useful for hooking into close events
   */
  onClose?: () => void

  /** Required callback fired when Upload button is clicked, receives selected files
   * Useful for hooking into upload events
   */
  onUpload: (documents: File[]) => void

  /**
   * Callback fired when dialog open state changes (when hasTrigger=false)
   * Used for external state management - receives the new open state
   */
  onOpenChange?: (open: boolean) => void

  /** Optional validator fired before upload. Return false to abort. */
  onValidateBeforeUpload?: () => boolean | Promise<boolean>
}

const CardContent = ({ children }: { children: ReactNode }) => {
  return children
}

const FileListContent = ({ children }: { children: ReactNode }) => {
  return children
}

export const DocumentUploadDialog = ({
  children = null,
  uploadButtonOnly = false,
  cardDescription = '',
  cardTitle = '',
  hasTrigger = false,
  isOpen = false,
  isUploadDisabled = false,
  label,
  afterLabel,
  onClose,
  onUpload,
  onOpenChange,
  onValidateBeforeUpload,
}: IDocumentUploadDialog) => {
  const [documentsToUpload, setDocumentsToUpload] = useState<File[]>([])
  const [internalOpen, setInternalOpen] = useState(false)

  // Use external control when isOpen is provided, otherwise use internal state
  const open = hasTrigger ? internalOpen : isOpen

  const handleOpenChange = (newOpen: boolean) => {
    if (hasTrigger) {
      setInternalOpen(newOpen)
    } else {
      onOpenChange?.(newOpen)
    }
  }

  const handleClose = () => {
    setDocumentsToUpload([])

    if (hasTrigger) {
      setInternalOpen(false)
    }

    onClose?.()
  }

  const handleTriggerClick = () => {
    if (hasTrigger) {
      setInternalOpen(true)
    }
  }

  const handleFilesUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    setDocumentsToUpload((prev) => [...prev, ...files])
  }

  const handleUpload = async () => {
    if (onValidateBeforeUpload && !(await onValidateBeforeUpload())) {
      return
    }

    onUpload(documentsToUpload)
    setDocumentsToUpload([])
    onClose?.()

    if (hasTrigger) {
      setInternalOpen(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {label && !uploadButtonOnly && (
        <Label className="flex items-center">
          {label}
          {afterLabel}
        </Label>
      )}

      {hasTrigger && (
        <Dialog.Trigger asChild>
          <Button
            iconLeft={faUpload}
            className="w-fit"
            size={uploadButtonOnly ? 'sm' : 'md'}
            variant={uploadButtonOnly ? 'secondary' : 'primary'}
            onClick={handleTriggerClick}
          >
            Upload Documents
          </Button>
        </Dialog.Trigger>
      )}

      <Dialog.Content onDismiss={handleClose}>
        <Dialog.Title>Upload documents</Dialog.Title>

        <Card color={CardColors.LEVEL_1}>
          {cardTitle && (
            <div className="flex items-center gap-2">
              <Icon icon={faCloudArrowUp} size="sm" color="text-neutral-700" />
              <Text variant={TypographyStyles.LABEL_MD}>{cardTitle}</Text>
            </div>
          )}

          {cardDescription && (
            <Text variant={TypographyStyles.BODY_SM}>{cardDescription}</Text>
          )}

          {React.Children.toArray(children).find(
            (child) => React.isValidElement(child) && child.type === CardContent
          )}
        </Card>

        {React.Children.toArray(children).filter(
          (child) =>
            !React.isValidElement(child) ||
            (child.type !== CardContent && child.type !== FileListContent)
        )}

        <FileInput onChange={handleFilesUpdate} disabled={isUploadDisabled}>
          {(() => {
            const fileListChild = React.Children.toArray(children).find(
              (child) =>
                React.isValidElement(child) && child.type === FileListContent
            )

            if (React.isValidElement(fileListChild)) {
              // Clone the child and pass files to its children
              return React.cloneElement(
                fileListChild as React.ReactElement<{
                  children?: React.ReactNode
                }>,
                {
                  children: React.isValidElement(fileListChild.props.children)
                    ? React.cloneElement(
                        fileListChild.props.children as React.ReactElement<{
                          files?: File[]
                          onRemove?: (index: number) => void
                        }>,
                        {
                          files: documentsToUpload,
                          onRemove: (index: number) => {
                            const newFiles = documentsToUpload.filter(
                              (_, i) => i !== index
                            )
                            setDocumentsToUpload(newFiles)
                          },
                        }
                      )
                    : fileListChild.props.children,
                }
              )
            }

            return fileListChild
          })()}
        </FileInput>

        <Dialog.Footer>
          <Dialog.Close asChild>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
          </Dialog.Close>

          <Button variant="primary" onClick={handleUpload}>
            Upload
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

DocumentUploadDialog.CardContent = CardContent
DocumentUploadDialog.FileListContent = FileListContent
